import React, { useEffect, useMemo, useState } from 'react';
import { LOCALE_KEY } from '@fx-ui/intl';
import {
    Avatar,
    Menu,
    MenuItemProps,
    Popover,
    RightOutlined,
    Spin,
    SvgIcon,
    Tag
} from '@fx-ui/fine-design';
import { EMPTY_FN, isUndefined, map } from '@libs/shared';
import { WWOpenDataCom } from '../../components';
import { getLocaleList, logout, updateUserLocale } from '../../apis';
import { Exit } from '../../icons';
import { authModel, globalState } from '../../models';
import { platformIntl } from '../../constants';
import LocaleIcon from '../../assets/svgicons/locale.svg';
import { switchLanguage } from '../../utils';

interface UserPanelProps {
    avatarSize?: 'large' | 'small' | number;
    items: {
        content: React.ReactNode;
    }[];
    onTriggerClick?: () => void;
    onMenuClick?: (menuKey: string, menuValue?: any) => void;
    /** 自定义退出登录实现 */
    onSignOut?: () => void;
    /** item上是否显示icon */
    showIcon?: boolean;
}

const UserPanel: React.FC<UserPanelProps> = (props) => {
    const {
        avatarSize = 24,
        items,
        onTriggerClick = EMPTY_FN,
        onMenuClick = EMPTY_FN,
        showIcon = false,
        onSignOut
    } = props;

    const {
        memberNickname,
        memberAvatar,
        currentCorpName,
        isCorpOwner,
        isISVEncrypted,
        isExternalMember
    } = authModel;
    const [localeList, setLocaleList] = useState<{ label: string; value: LOCALE_KEY }[]>();
    const [currentLocaleKey, setCurrentLocaleKey] = useState<LOCALE_KEY>(
        globalState.localeKey ?? globalState.defaultLocaleKey!
    );
    const currentLocaleLabel = useMemo(() => {
        return localeList?.find((item) => item.value === currentLocaleKey)?.label;
    }, [localeList, currentLocaleKey]);

    const getMenuItems = () => {
        const menus: MenuItemProps[] = [
            {
                className: 'no-hover',
                content: (
                    <div className="user-info">
                        <div className="nickname">
                            {isExternalMember ? (
                                memberNickname
                            ) : (
                                <WWOpenDataCom openId={memberNickname}>
                                    {memberNickname}
                                </WWOpenDataCom>
                            )}
                        </div>
                        <div className="user-corp">
                            <div className="corp-name">{currentCorpName}</div>
                            {currentCorpName && (
                                <Tag className="corp-tag" shape="roundedRec">
                                    {isCorpOwner
                                        ? platformIntl.get('corp.my.create')
                                        : platformIntl.get('corp.my.join')}
                                </Tag>
                            )}
                        </div>
                    </div>
                )
            },
            {
                isDivider: true
            },
            ...map(items, (item) => ({
                ...item,
                className: 'user-panel-item'
            })),
            {
                isDivider: true
            },
            {
                className: 'user-panel-item',
                content: (
                    <div className="locale-item nav-item">
                        {showIcon && <SvgIcon component={LocaleIcon} className="nav-item-icon" />}
                        <span className="locale-title">
                            {platformIntl.get('fxp.base.workspace.profile.language.nm')}
                        </span>
                        <div className="locale-value">
                            {isUndefined(localeList) ? (
                                <Spin size="small" style={{ position: 'relative' }} />
                            ) : (
                                <>
                                    <div className="text">{currentLocaleLabel}</div>
                                    <RightOutlined className="arrow-icon" />
                                </>
                            )}
                        </div>
                    </div>
                ),
                children: map(localeList, (item) => ({
                    ...item,
                    selected: item.value === currentLocaleKey,
                    onSelect: handleLocaleSelect
                }))
            }
        ];

        /** 退出登录 */
        if (!isISVEncrypted) {
            menus.push(
                ...[
                    {
                        isDivider: true
                    },
                    {
                        className: 'user-panel-item',
                        content: (
                            <div
                                className="nav-item"
                                onClick={() => {
                                    onMenuClick('exit');
                                    onSignOut ? onSignOut() : logout();
                                }}
                            >
                                {showIcon && <Exit className="exit-icon nav-item-icon" />}
                                <span>{platformIntl.get('signout')}</span>
                            </div>
                        )
                    }
                ]
            );
        }

        return menus;
    };

    const handleLocaleSelect = (value: LOCALE_KEY) => {
        onMenuClick('language', value);
        if (value !== currentLocaleKey) {
            updateUserLocale(value).then(() => {
                setCurrentLocaleKey(value);
                switchLanguage(value);
                window.location.reload();
            });
        }
    };

    useEffect(() => {
        getLocaleList().then((res) => {
            setLocaleList(Array.isArray(res) ? res : []);
        });
    }, []);

    return (
        <Popover
            content={
                <Menu
                    className="fx-user-panel-content"
                    menuWidth={[260, 180]}
                    items={getMenuItems()}
                    onAfterSelect={(item) => item.onSelect?.(item.value)}
                />
            }
            offset={[6, 8]}
            placement="bottom-right"
            trigger="click"
            destroyTooltipOnHide={false}
            arrow={false}
            hasPadding={false}
        >
            <div
                className="fx-user-panel"
                onClick={() => {
                    onTriggerClick();
                }}
            >
                <Avatar
                    className="user-avatar"
                    name={memberNickname}
                    img={memberAvatar}
                    style={{ fontSize: '12px' }}
                    size={avatarSize}
                />
            </div>
        </Popover>
    );
};

export default UserPanel;
